import React, { Suspense, useState } from 'react';
import { EditOutlined, DeleteOutlined, SmileOutlined } from '@ant-design/icons';
import Icon from '@ant-design/icons';
import { Popover } from 'antd';
import CustomTooltip from '../../../../components/UI/Tooltip/CustomTooltip';
import { CommentBubbleIcon } from './commentIcons';

const EmojiPickerPopover = React.lazy(() => import('./EmojiPickerPopover'));

// Three default quick reactions shown inline on hover (matches Figma + DEV-742).
const QUICK_REACTIONS: Array<{ emoji: string; label: string }> = [
  { emoji: '👍', label: 'Agree' },
  { emoji: '👀', label: 'Looking at it…' },
  { emoji: '🚀', label: 'Ship it!' },
];

// Shared icon-button styling (the app's convention: padded Ant icon with a
// subtle hover background that adapts to dark mode via CSS variables).
const ICON_BTN = 'p-0.5 text-base text-neutral-900 cursor-pointer hover:bg-neutral-200 rounded';

interface CommentToolbarProps {
  isOwner: boolean;
  viewerReactedEmojis: Set<string>;
  onToggleReaction: (emoji: string) => void;
  onReply?: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

export const CommentToolbar: React.FC<CommentToolbarProps> = ({
  isOwner,
  viewerReactedEmojis,
  onToggleReaction,
  onReply,
  onEdit,
  onDelete,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);

  return (
    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center gap-1 bg-brand-white border border-neutral-200 rounded p-1 shadow-[0px_4px_5px_rgba(112,119,146,0.2)]">
      {QUICK_REACTIONS.map(({ emoji, label }) => (
        <CustomTooltip key={emoji} title={label}>
          <span
            role="button"
            tabIndex={0}
            onClick={() => onToggleReaction(emoji)}
            className={`inline-flex items-center justify-center p-0.5 rounded text-sm leading-none cursor-pointer hover:bg-neutral-200 ${
              viewerReactedEmojis.has(emoji) ? 'bg-blue-100' : ''
            }`}
          >
            {emoji}
          </span>
        </CustomTooltip>
      ))}

      <Popover
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        trigger="click"
        placement="bottomRight"
        overlayInnerStyle={{ padding: 0 }}
        content={
          <Suspense fallback={<div className="w-[320px] h-[400px] flex items-center justify-center">…</div>}>
            <EmojiPickerPopover
              onSelect={(emoji) => {
                onToggleReaction(emoji);
                setPickerOpen(false);
              }}
            />
          </Suspense>
        }
      >
        <CustomTooltip title="All reactions">
          <SmileOutlined className={ICON_BTN} />
        </CustomTooltip>
      </Popover>

      {onReply && (
        <CustomTooltip title="Reply to comment">
          <Icon component={CommentBubbleIcon} className={ICON_BTN} onClick={onReply} />
        </CustomTooltip>
      )}

      {isOwner && (
        <>
          <CustomTooltip title="Edit comment">
            <EditOutlined className={ICON_BTN} onClick={onEdit} />
          </CustomTooltip>
          <CustomTooltip title="Delete comment">
            <DeleteOutlined className={ICON_BTN} onClick={onDelete} />
          </CustomTooltip>
        </>
      )}
    </div>
  );
};
